import isEmpty from 'lodash/isEmpty';

import { notifyAlert, notifySuccess, notifyFailure } from 'app/Notifications/actions';

import { MOST_RECENT_COMPONENT_COUNT } from './const';
import {
  favoriteContentSuccess,
  getUserFailure,
  getUserRequest,
  getUserSuccess,
  mostRecentComponentsFailure,
  mostRecentComponentsSuccess
} from './actions';
import lanIdSelector from './selectors/lanId';
import userSelector from './selectors/user';

/**
 * get most recently created components.
 * @returns {undefined}
 */
export const getMostRecentComponentsThunk = () => async (dispatch, getState, { api }) => {
  try {
    const state = getState();
    const lanId = lanIdSelector(state);
    const response = await api.search.getMostRecentComponents(
      lanId,
      MOST_RECENT_COMPONENT_COUNT
    );
    dispatch(mostRecentComponentsSuccess(response));
  } catch (error) {
    dispatch(mostRecentComponentsFailure(error));
  }
};

/**
 * get user data if missing
 *
 * @returns {Function} thunk to get user data
 */
export const getUserDataThunk = function getUserDataThunk() {
  return async (dispatch, getState, { api }) => {
    // skip request if user data already exists
    if (!isEmpty(userSelector(getState()))) {
      return;
    }

    try {
      dispatch(getUserRequest());
      const data = await api.user.getUser();
      dispatch(getUserSuccess(data));
      dispatch(getMostRecentComponentsThunk());
    } catch (error) {
      dispatch(getUserFailure(error));
    }
  };
};

/**
 * attempts to favorite content and refreshes entire tree.
 * @param {string} contentName - name of content
 * @param {string} contentType - content type
 * @param {string} contentId - id of content
 * @param {string} locale - content locale
 * @returns {undefined}
 */
export const favoriteContentThunk = (contentName, contentType, contentId, locale) => async (
  dispatch,
  getState,
  { api }
) => {
  dispatch(notifyAlert(`Adding ${contentType} ${contentName} to favorites.`));
  try {
    const favoriteContent = await api.user.favoriteContent(contentId, contentType, locale);
    dispatch(favoriteContentSuccess(favoriteContent));
    dispatch(notifySuccess(`${contentName} has been added to favorites successfully.`));
  } catch (error) {
    dispatch(notifyFailure(`Favoriting ${contentName} failed: ${error}`));
  }
};

/**
 * attempts to unfavorite content and refreshes entire tree.
 * @param {string} contentName - name of content
 * @param {string} contentType - content type
 * @param {string} contentId - id of content
 * @param {string} locale - content locale
 * @returns {undefined}
 */
export const unfavoriteContentThunk = (contentName, contentType, contentId, locale) => async (
  dispatch,
  getState,
  { api }
) => {
  dispatch(notifyAlert(`Removing ${contentType} ${contentName} from favorites.`));
  try {
    const favoriteContent = await api.user.unfavoriteContent(contentId, contentType, locale);
    dispatch(favoriteContentSuccess(favoriteContent));
    dispatch(notifySuccess(`${contentName} has been removed from favorites successfully.`));
  } catch (error) {
    dispatch(notifyFailure(`Unfavoriting ${contentName} failed: ${error}`));
  }
};

/**
 * toggles favorite state of a content
 * @param {boolean} isFavorite - current favorite state of the content
 * @param {string} contentName - name of the content
 * @param {string} contentType - type of the content
 * @param {string} contentId - id of the content
 * @param {string} locale - of the content
 * @returns {undefined}
 */
export const toggleFavoriteContentThunk = (
  isFavorite,
  contentName,
  contentType,
  contentId,
  locale
) => async dispatch => {
  if (isFavorite) {
    dispatch(unfavoriteContentThunk(contentName, contentType, contentId, locale));
  } else {
    dispatch(favoriteContentThunk(contentName, contentType, contentId, locale));
  }
};
